import { Badge } from '@voll-intelligence/ui-kit/ui/badge';
import { Checkbox } from '@voll-intelligence/ui-kit/ui/checkbox';
import type { CheckoutTravelDocs } from '@voll-intelligence/genui-contract';
import { countryDisplayName } from '../../../../shared/ui/countries.js';
import { useGenUiI18n } from '../../../../shared/ui/i18n.js';

const STATUS_COLOR = { required: 'danger', attention: 'warning', ok: 'success' } as const;

/** Os documentos que o destino pede (Timatic ou o aviso de base indisponível).
 *  É aviso, nunca trava: a marcação «Tenho» só vai junto no envio. */
export function TravelDocsSection({
  travelDocs,
  checked,
  onToggle,
  readOnly = false,
}: Readonly<{
  travelDocs: CheckoutTravelDocs;
  checked: readonly string[];
  onToggle: (id: string) => void;
  readOnly?: boolean;
}>) {
  const { t, locale } = useGenUiI18n();
  return (
    <section className="grid gap-3" aria-label={t('checkoutTravelDocs.title')}>
      <header className="grid gap-0.5">
        <h4 className="text-sm font-semibold">{t('checkoutTravelDocs.title')}</h4>
        <small className="text-xs text-muted-foreground">
          {travelDocs.source === 'timatic'
            ? t('checkoutTravelDocs.source')
            : t('checkoutTravelDocs.fallback')}
        </small>
      </header>
      {travelDocs.blocks.map((block) => {
        const country = countryDisplayName(block.country, locale) ?? block.country;
        return (
          <div key={`${block.applicability}:${block.country}`} className="grid gap-2">
            <p className="text-xs font-medium text-muted-foreground">
              {t(`checkoutTravelDocs.${block.applicability}`, { country })}
            </p>
            <ul className="grid gap-2">
              {block.items.map((item) => (
                <li key={item.id} className="grid gap-1">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={checked.includes(item.id)}
                      disabled={readOnly}
                      onCheckedChange={() => {
                        onToggle(item.id);
                      }}
                      aria-label={`${item.title} · ${country} — ${t('checkoutTravelDocs.have')}`}
                    />
                    <span className="flex-1 text-sm">{item.title}</span>
                    <Badge variant="soft" color={STATUS_COLOR[item.status]} className="shrink-0">
                      {t(`checkoutTravelDocs.status.${item.status}`)}
                    </Badge>
                  </div>
                  {item.detail ? (
                    <details className="ps-6 text-xs text-muted-foreground">
                      <summary className="cursor-pointer">{t('checkoutTravelDocs.detail')}</summary>
                      <p>{item.detail}</p>
                    </details>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <small className="text-xs text-muted-foreground">{t('checkoutTravelDocs.footer')}</small>
    </section>
  );
}
